import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { orgKey } from '@/app/queryClient';
import { PIPELINE_ORDER, type PipelineState } from '@/components/ui';
import { formatNumber } from '@/lib/format';

type S = components['schemas'];
export type Location = S['Location'];
export type LocationSummary = S['LocationSummary'];
export type LocationCreate = S['LocationCreate'];
export type LocationChanges = S['LocationChanges'];
export type LocationRevision = S['LocationRevision'];
export type LocationPage = S['LocationPage'];
export type AutomationMode = S['LocationAutomationMode'];
export type HandoffState = S['HandoffState'];
export type FieldDefinition = S['FieldDefinition'];
export type Conversation = S['Conversation'];

/** Board columns shown by default. */
export const ACTIVE_STAGES = [
  'ingested',
  'researching',
  'enriching',
  'engaged',
  'ready_for_serious_engagement',
] as const satisfies readonly PipelineState[];
/** Hidden behind "Show closed"; the API also leaves them out unless include_excluded is set. */
export const CLOSED_STAGES = ['disqualified', 'cancelled', 'archived'] as const satisfies readonly PipelineState[];

/** Targets with consequences beyond the stage: they always open the confirm dialog with a reason. */
export const GUARDED_TARGETS: readonly PipelineState[] = ['ready_for_serious_engagement', 'cancelled', 'disqualified', 'archived'];

export interface LocationListParams {
  q?: string;
  pipeline_state?: PipelineState[];
  country_code?: string;
  company_id?: string;
  include_excluded?: boolean;
  sort?: string;
  limit: number;
  cursor?: string;
}

/** A list that keeps loading as the user scrolls: one board lane, or the list view. */
export type FeedParams = Omit<LocationListParams, 'cursor'> & { sort: string };

export const locationKeys = {
  list: (orgId: string, p: LocationListParams) => orgKey(orgId, 'locations', 'list', p),
  lists: (orgId: string) => orgKey(orgId, 'locations', 'list'),
  feed: (orgId: string, p: FeedParams) => orgKey(orgId, 'locations', 'feed', p),
  feeds: (orgId: string) => orgKey(orgId, 'locations', 'feed'),
  facets: (orgId: string) => orgKey(orgId, 'locations', 'facets'),
  detail: (orgId: string, id: string) => orgKey(orgId, 'locations', 'detail', id),
  history: (orgId: string, id: string) => orgKey(orgId, 'locations', 'detail', id, 'history'),
  fields: (orgId: string) => orgKey(orgId, 'field-definitions'),
};

/**
 * What a change to one site (or a new site) can alter: its record and history, every lane and
 * list, the filter options. Never every location record: each card thumbnail holds one, and
 * refetching them all would send a request per loaded card.
 */
export function siteChangedKeys(orgId: string, id?: string) {
  return [...(id ? [locationKeys.detail(orgId, id)] : []), locationKeys.feeds(orgId), locationKeys.lists(orgId), locationKeys.facets(orgId)];
}

export function locationListQuery(orgId: string, p: LocationListParams) {
  return queryOptions({
    queryKey: locationKeys.list(orgId, p),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/locations', {
          params: {
            path: { orgId },
            query: {
              q: p.q || undefined,
              pipeline_state: p.pipeline_state?.length ? p.pipeline_state : undefined,
              country_code: p.country_code,
              company_id: p.company_id,
              include_excluded: p.include_excluded || undefined,
              sort: p.sort,
              limit: p.limit,
              cursor: p.cursor,
            },
          },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** Cards per lane page: a few screens of cards; the next page loads before the end is reached. */
export const LANE_PAGE_SIZE = 30;

/**
 * Cursor pages of locations. The first page carries the exact total for these filters (the
 * lane count); later pages skip it.
 */
export function locationFeedQuery(orgId: string, p: FeedParams) {
  return infiniteQueryOptions({
    queryKey: locationKeys.feed(orgId, p),
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/locations', {
          params: {
            path: { orgId },
            query: {
              q: p.q || undefined,
              pipeline_state: p.pipeline_state?.length ? p.pipeline_state : undefined,
              country_code: p.country_code,
              company_id: p.company_id,
              include_excluded: p.include_excluded || undefined,
              sort: p.sort,
              limit: p.limit,
              cursor: pageParam,
              include_total: pageParam ? undefined : true,
            },
          },
          signal,
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined),
    placeholderData: keepPreviousData,
  });
}

/** Every location once (including closed), for the country and owner filter options. */
export function facetsQuery(orgId: string) {
  return queryOptions({
    queryKey: locationKeys.facets(orgId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/locations', {
          params: { path: { orgId }, query: { include_excluded: true, limit: 200, sort: 'name' } },
          signal,
        }),
      ),
    staleTime: 5 * 60_000,
    select: (page) => {
      const countries = new Set<string>();
      const owners = new Map<string, string>();
      for (const l of page.items) {
        if (l.country_code) countries.add(l.country_code);
        if (l.primary_company_id && l.primary_company_name) owners.set(l.primary_company_id, l.primary_company_name);
      }
      return {
        countries: [...countries].sort((a, b) => countryName(a).localeCompare(countryName(b))),
        owners: [...owners].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      };
    },
  });
}

export function locationQuery(orgId: string, locationId: string) {
  return queryOptions({
    queryKey: locationKeys.detail(orgId, locationId),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/locations/{locationId}', { params: { path: { orgId, locationId } }, signal })),
  });
}

/** Labels and units for location facts (building_m2 is "Building area", m2). */
export function fieldDefinitionsQuery(orgId: string) {
  return queryOptions({
    queryKey: locationKeys.fields(orgId),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/field-definitions', { params: { path: { orgId }, query: { limit: 200 } }, signal })),
    staleTime: 10 * 60_000,
    select: (page) => new Map(page.items.map((d) => [d.key, d])),
  });
}

/**
 * Conversations, newest message first; optionally for one location. Keyed under the
 * conversations namespace so approving or sending in a thread refreshes the board too.
 * The board and list use one org-wide call (200 newest) mapped by primary location: the API has
 * no multi-location filter, and one request is cheaper than one per card. A location whose
 * threads are older than those 200 shows its last activity instead.
 */
export function conversationsQuery(orgId: string, locationId?: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'conversations', 'list', { location_id: locationId ?? null, scope: 'locations' }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/conversations', {
          params: { path: { orgId }, query: { location_id: locationId, limit: 200, sort: '-last_message_at' } },
          signal,
        }),
      ),
  });
}

export function isPipelineState(v: unknown): v is PipelineState {
  return typeof v === 'string' && (PIPELINE_ORDER as readonly string[]).includes(v);
}

const regionNames = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(undefined, { type: 'region' }) : null;

/** "Germany" for DE; the code itself when the browser has no name for it. */
export function countryName(code: string): string {
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function locationPlace(l: { locality: string | null; country_code: string | null }): string {
  return [l.locality, l.country_code].filter(Boolean).join(', ');
}

/** Power in MW from the canonical kW decimal: "12 MW", "0.5 MW". */
export function formatMw(kw: string | null | undefined): string | null {
  if (kw == null || kw === '') return null;
  const n = Number(kw);
  if (!Number.isFinite(n)) return null;
  return formatNumber(n / 1000, 'MW');
}
